import React from 'react';
import {
  VStack,
  HStack,
  Text,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  Input,
  SimpleGrid,
  Icon,
  useColorModeValue,
  Badge,
  InputGroup,
  InputLeftAddon,
  Box,
  Divider,
} from '@chakra-ui/react';
import { FiMail, FiPhone, FiUser, FiUsers, FiShoppingCart, FiDollarSign } from 'react-icons/fi';
import { RegistrationData } from '../index';

interface ContactInformationProps {
  data: RegistrationData;
  updateData: (data: RegistrationData) => void;
  onNext: () => void;
  onPrevious: () => void;
}

const ContactInformation: React.FC<ContactInformationProps> = ({
  data,
  updateData,
}) => {
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const contactSuccessBg = useColorModeValue('green.50', 'green.900');
  const infoBg = useColorModeValue('blue.50', 'blue.900');

  const updateContactInfo = (field: string, value: string) => {
    updateData({
      ...data,
      contactInfo: {
        ...data.contactInfo,
        [field]: value,
      },
    });
  };

  const updateNestedContactInfo = (section: string, field: string, value: string) => {
    const currentContactInfo = data.contactInfo || {};
    
    let updatedSection: any = {};
    
    if (section === 'customerSupport') {
      updatedSection = { ...currentContactInfo.customerSupport, [field]: value };
    } else if (section === 'accounting') {
      updatedSection = { ...currentContactInfo.accounting, [field]: value };
    }
    
    updateData({
      ...data,
      contactInfo: {
        ...currentContactInfo,
        [section]: updatedSection,
      },
    });
  };

  const isFormValid = () => {
    return data.contactInfo?.poEmail && 
           data.contactInfo?.customerSupport?.email && 
           data.contactInfo?.accounting?.email;
  };

  return (
    <VStack spacing={8} align="stretch">

      {/* Purchase Order Email */}
      <Card>
        <CardBody p={8}>
          <VStack spacing={6} align="stretch">
            <HStack spacing={3}>
              <Icon as={FiShoppingCart} color="blue.500" boxSize={5} />
              <Text fontSize="lg" fontWeight="semibold">
                Order Management
              </Text>
            </HStack>
            
            <FormControl isRequired>
              <FormLabel>Purchase Order Email Address</FormLabel>
              <Text fontSize="sm" color="gray.600" mb={3}>
                Where should we send purchase orders and order confirmations?
              </Text>
              <InputGroup>
                <InputLeftAddon>
                  <Icon as={FiMail} />
                </InputLeftAddon>
                <Input
                  type="email"
                  placeholder="orders@yourcompany.com"
                  value={data.contactInfo?.poEmail || ''}
                  onChange={(e) => updateContactInfo('poEmail', e.target.value)}
                />
              </InputGroup>
            </FormControl>
          </VStack>
        </CardBody>
      </Card>

      {/* Department Contacts */}
      <Card>
        <CardBody p={8}>
          <VStack spacing={8} align="stretch">
            <HStack spacing={3}>
              <Icon as={FiUsers} color="blue.500" boxSize={5} />
              <Text fontSize="lg" fontWeight="semibold">
                Department Contacts
              </Text>
            </HStack>

            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8}>
              {/* Customer Support Contact */}
              <Box>
                <VStack spacing={6} align="stretch">
                  <HStack spacing={3} mb={2}>
                    <Icon as={FiUser} color="green.500" boxSize={4} />
                    <Text fontWeight="semibold" color="green.600">
                      Customer Support
                    </Text>
                  </HStack>
                  
                  <SimpleGrid columns={2} spacing={4}>
                    <FormControl>
                      <FormLabel>First Name</FormLabel>
                      <Input
                        placeholder="First name"
                        value={data.contactInfo?.customerSupport?.firstName || ''}
                        onChange={(e) => updateNestedContactInfo('customerSupport', 'firstName', e.target.value)}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Last Name</FormLabel>
                      <Input
                        placeholder="Last name"
                        value={data.contactInfo?.customerSupport?.lastName || ''}
                        onChange={(e) => updateNestedContactInfo('customerSupport', 'lastName', e.target.value)}
                      />
                    </FormControl>
                  </SimpleGrid>

                  <FormControl>
                    <FormLabel>Email Address</FormLabel>
                    <InputGroup>
                      <InputLeftAddon>
                        <Icon as={FiMail} />
                      </InputLeftAddon>
                      <Input
                        type="email"
                        placeholder="support@yourcompany.com"
                        value={data.contactInfo?.customerSupport?.email || ''}
                        onChange={(e) => updateNestedContactInfo('customerSupport', 'email', e.target.value)}
                      />
                    </InputGroup>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Phone Number</FormLabel>
                    <InputGroup>
                      <InputLeftAddon>
                        <Icon as={FiPhone} />
                      </InputLeftAddon>
                      <Input
                        type="tel"
                        placeholder="(555) 123-4567"
                        value={data.contactInfo?.customerSupport?.phone || ''}
                        onChange={(e) => updateNestedContactInfo('customerSupport', 'phone', e.target.value)}
                      />
                    </InputGroup>
                  </FormControl>
                </VStack>
              </Box>

              <Box>
                {/* Divider for mobile */}
                <Box display={{ base: 'block', lg: 'none' }} py={4}>
                  <Divider />
                </Box>
                
                {/* Accounting Contact */}
                <VStack spacing={6} align="stretch">
                  <HStack spacing={3} mb={2}>
                    <Icon as={FiDollarSign} color="purple.500" boxSize={4} />
                    <Text fontWeight="semibold" color="purple.600">
                      Accounting & Billing
                    </Text>
                  </HStack>
                  
                  <SimpleGrid columns={2} spacing={4}>
                    <FormControl>
                      <FormLabel>First Name</FormLabel>
                      <Input
                        placeholder="First name"
                        value={data.contactInfo?.accounting?.firstName || ''}
                        onChange={(e) => updateNestedContactInfo('accounting', 'firstName', e.target.value)}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Last Name</FormLabel>
                      <Input
                        placeholder="Last name"
                        value={data.contactInfo?.accounting?.lastName || ''}
                        onChange={(e) => updateNestedContactInfo('accounting', 'lastName', e.target.value)}
                      />
                    </FormControl>
                  </SimpleGrid>

                  <FormControl>
                    <FormLabel>Email Address</FormLabel>
                    <InputGroup>
                      <InputLeftAddon>
                        <Icon as={FiMail} />
                      </InputLeftAddon>
                      <Input
                        type="email"
                        placeholder="accounting@yourcompany.com"
                        value={data.contactInfo?.accounting?.email || ''}
                        onChange={(e) => updateNestedContactInfo('accounting', 'email', e.target.value)}
                      />
                    </InputGroup>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Phone Number</FormLabel>
                    <InputGroup>
                      <InputLeftAddon>
                        <Icon as={FiPhone} />
                      </InputLeftAddon>
                      <Input
                        type="tel"
                        placeholder="(555) 123-4567"
                        value={data.contactInfo?.accounting?.phone || ''}
                        onChange={(e) => updateNestedContactInfo('accounting', 'phone', e.target.value)}
                      />
                    </InputGroup>
                  </FormControl>
                </VStack>
              </Box>
            </SimpleGrid>
          </VStack>
        </CardBody>
      </Card>

      {/* Information Note */}
      <Card bg={infoBg} borderColor="blue.200">
        <CardBody>
          <HStack spacing={3}>
            <Icon as={FiMail} color="blue.500" />
            <VStack align="start" spacing={1}>
              <Text fontSize="sm" fontWeight="semibold" color="blue.700">
                How we'll use these contacts
              </Text>
              <Text fontSize="sm" color="blue.600">
                • <strong>Orders:</strong> Purchase confirmations and shipping updates
                • <strong>Support:</strong> Project questions and technical assistance  
                • <strong>Billing:</strong> Invoices and payment-related communications
              </Text>
            </VStack>
          </HStack>
        </CardBody>
      </Card>

      {/* Progress Indicator */}
      {isFormValid() && (
        <Card bg={contactSuccessBg} borderColor="green.200">
          <CardBody>
            <HStack spacing={3}>
              <Icon as={FiUsers} color="green.500" />
              <Text fontSize="sm" color="green.700" fontWeight="medium">
                Contact information completed
              </Text>
              <Badge colorScheme="green" ml="auto">
                Ready to continue
              </Badge>
            </HStack>
          </CardBody>
        </Card>
      )}
    </VStack>
  );
};

export default ContactInformation; 